import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  CardMedia,
  Fab,
  Typography,
  Collapse,
  IconButton,
  Divider,
  Stack,
  useTheme,
  alpha,
  Zoom,
  Fade,
} from '@mui/material';
import { styled, keyframes } from '@mui/material/styles';
import Iconify from '../iconify';

const pulseAnimation = keyframes`
  0% {
    box-shadow: 0 0 0 0 rgba(54, 179, 126, 0.7);
  }
  70% {
    box-shadow: 0 0 0 10px rgba(54, 179, 126, 0);
  }
  100% {
    box-shadow: 0 0 0 0 rgba(54, 179, 126, 0);
  }
`;

const StyledFab = styled(Fab)(({ theme, open }) => ({
  position: 'fixed',
  bottom: 24,
  right: 24,
  zIndex: 1100,
  width: 56,
  height: 56,
  backgroundColor: theme.palette.primary.main,
  color: theme.palette.primary.contrastText,
  boxShadow: `0 8px 32px ${alpha(theme.palette.primary.main, 0.4)}`,
  animation: !open ? `${pulseAnimation} 2s infinite` : 'none',
  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
  '&:hover': {
    backgroundColor: theme.palette.primary.dark,
    transform: 'scale(1.05)',
    boxShadow: `0 12px 40px ${alpha(theme.palette.primary.main, 0.5)}`,
  },
  '&:active': {
    transform: 'scale(0.95)',
  },
}));

const StyledCard = styled(Card)(({ theme }) => ({
  position: 'fixed',
  bottom: 96,
  right: 24,
  width: 360,
  zIndex: 1100,
  backgroundColor: theme.palette.background.paper,
  borderRadius: 16,
  border: `1px solid ${alpha(theme.palette.grey[300], 0.2)}`,
  boxShadow: `
    0 24px 48px ${alpha(theme.palette.grey[900], 0.12)},
    0 12px 24px ${alpha(theme.palette.grey[900], 0.08)}
  `,
  overflow: 'hidden',
}));

const VideoCard = styled(Card)(({ theme }) => ({
  cursor: 'pointer',
  position: 'relative',
  borderRadius: 12,
  overflow: 'hidden',
  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
  border: `1px solid ${alpha(theme.palette.grey[300], 0.2)}`,
  background: theme.palette.background.paper,
  '&:hover': {
    transform: 'translateY(-4px) scale(1.02)',
    boxShadow: `0 16px 32px ${alpha(theme.palette.grey[900], 0.16)}`,
    '& .play-overlay': {
      background: alpha(theme.palette.common.black, 0.5),
    },
    '& .video-thumbnail': {
      transform: 'scale(1.05)',
    },
  },
  '&:active': {
    transform: 'translateY(-2px) scale(0.98)',
  },
}));

const PlayOverlay = styled(Box)(({ theme }) => ({
  position: 'absolute',
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  background: alpha(theme.palette.common.black, 0.3),
  opacity: 1,
  transform: 'scale(1)',
  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
  zIndex: 2,
}));

const PlayButton = styled(Box)(({ theme }) => ({
  width: 60,
  height: 60,
  borderRadius: '50%',
  backgroundColor: theme.palette.common.white,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  color: theme.palette.common.black,
  boxShadow: `0 8px 24px ${alpha(theme.palette.common.black, 0.3)}`,
  transition: 'all 0.2s ease-in-out',
  '&:hover': {
    transform: 'scale(1.1)',
    backgroundColor: theme.palette.common.white,
    boxShadow: `0 12px 32px ${alpha(theme.palette.common.black, 0.4)}`,
  },
}));

const DocumentationButton = styled(Box)(({ theme }) => ({
  padding: theme.spacing(2.5),
  cursor: 'pointer',
  borderRadius: 12,
  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(1.5),
  color: theme.palette.text.primary,
  position: 'relative',
  backgroundColor: alpha(theme.palette.grey[50], 0.5),
  border: `1px solid ${alpha(theme.palette.grey[300], 0.2)}`,
  '&:hover': {
    backgroundColor: alpha(theme.palette.primary.main, 0.08),
    color: theme.palette.primary.main,
    transform: 'translateY(-2px)',
    boxShadow: `0 8px 24px ${alpha(theme.palette.primary.main, 0.15)}`,
    borderColor: alpha(theme.palette.primary.main, 0.3),
    '& .doc-icon': {
      transform: 'scale(1.1) rotate(3deg)',
    },
    '& .external-icon': {
      transform: 'translateX(4px)',
    },
  },
  '&:active': {
    transform: 'translateY(0px)',
  },
}));

const HeaderSection = styled(Box)(({ theme }) => ({
  backgroundColor: alpha(theme.palette.primary.main, 0.06),
  padding: theme.spacing(2.5),
  margin: theme.spacing(-2, -2, 2, -2),
  borderBottom: `1px solid ${alpha(theme.palette.grey[300], 0.2)}`,
}));

const OrDivider = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(2),
  margin: theme.spacing(2, 0),
  '& .divider-line': {
    flex: 1,
    height: 1,
    background: `linear-gradient(90deg, transparent 0%, ${alpha(theme.palette.grey[400], 0.6)} 50%, transparent 100%)`,
  },
  '& .or-text': {
    background: theme.palette.background.paper,
    padding: theme.spacing(0.5, 1.5),
    borderRadius: 20,
    fontSize: '0.75rem',
    fontWeight: 600,
    color: theme.palette.text.secondary,
    border: `1px solid ${alpha(theme.palette.grey[300], 0.3)}`,
  },
}));

const HelpWidget = () => {
  const [open, setOpen] = useState(false);
  const theme = useTheme();

  // Check if user has seen the help widget before
  useEffect(() => {
    const hasSeenHelp = localStorage.getItem('flowbookings-help-widget-seen');
    if (!hasSeenHelp) {
      // First time user - auto open the widget
      setOpen(true);
    }
  }, []);

  const handleToggle = () => {
    if (open) {
      // If closing, save to localStorage
      handleClose();
    } else {
      // If opening, just toggle
      setOpen(true);
    }
  };

  const handleClose = () => {
    setOpen(false);
    // Mark that user has seen and interacted with the help widget
    localStorage.setItem('flowbookings-help-widget-seen', 'true');
  };

  const handleVideoClick = () => {
    window.open('https://www.youtube.com/watch?v=Smk55uTrGfI', '_blank', 'noopener,noreferrer');
  };

  const handleDocumentationClick = () => {
    window.open('https://docs.flowbookings.io/', '_blank', 'noopener,noreferrer');
  };

  // YouTube video thumbnail URL
  const videoThumbnail = 'https://img.youtube.com/vi/Smk55uTrGfI/maxresdefault.jpg';

  return (
    <>
      <Zoom in={open} timeout={400} style={{ transformOrigin: 'bottom right' }}>
        <StyledCard>
          <CardContent sx={{ p: 2 }}>
            <HeaderSection>
              <Box display="flex" justifyContent="space-between" alignItems="center">
                <Box display="flex" alignItems="center" gap={1.5}>
                  <Box
                    sx={{
                      width: 32,
                      height: 32,
                      borderRadius: '50%',
                      backgroundColor: theme.palette.primary.main,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: 'white',
                    }}
                  >
                    <Iconify icon="eva:question-mark-circle-fill" sx={{ fontSize: 18 }} />
                  </Box>
                  <Box>
                    <Typography variant="h6" color="text.primary" sx={{ fontWeight: 600 }}>
                      Need Help?
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Choose your preferred option
                    </Typography>
                  </Box>
                </Box>
                <IconButton 
                  size="small" 
                  onClick={handleClose}
                  sx={{
                    color: theme.palette.text.secondary,
                    '&:hover': {
                      backgroundColor: alpha(theme.palette.grey[500], 0.08),
                      color: theme.palette.text.primary,
                    },
                  }}
                >
                  <Iconify icon="eva:close-fill" />
                </IconButton>
              </Box>
            </HeaderSection>

            <Stack spacing={2.5}>
              {/* Video Option */}
              <VideoCard onClick={handleVideoClick}>
                <Box sx={{ position: 'relative', height: 140 }}>
                  <CardMedia
                    component="img"
                    height="140"
                    image={videoThumbnail}
                    alt="FlowBookings Tutorial Video"
                    className="video-thumbnail"
                    sx={{
                      objectFit: 'cover',
                      transition: 'transform 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                    }}
                  />
                  <PlayOverlay className="play-overlay">
                    <PlayButton>
                      <svg 
                        width="24" 
                        height="24" 
                        viewBox="0 0 24 24" 
                        fill="none"
                        style={{ marginLeft: '2px' }}
                      >
                        <path 
                          d="M8 5V19L19 12L8 5Z" 
                          fill="black"
                        />
                      </svg>
                    </PlayButton>
                  </PlayOverlay>
                  {/* Duration badge */}
                  <Box
                    sx={{
                      position: 'absolute',
                      bottom: 8,
                      right: 8,
                      background: alpha(theme.palette.common.black, 0.8),
                      color: 'white',
                      px: 1,
                      py: 0.5,
                      borderRadius: 1,
                      fontSize: '0.75rem',
                      fontWeight: 500,
                    }}
                  >
                    Tutorial
                  </Box>
                </Box>
                <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                  <Typography variant="subtitle1" color="text.primary" sx={{ fontWeight: 600, mb: 0.5 }}>
                    Watch Tutorial Video
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.4 }}>
                    Get started quickly with our step-by-step video guide
                  </Typography>
                </CardContent>
              </VideoCard>

              {/* Enhanced OR Divider */}
              <OrDivider>
                <Box className="divider-line" />
                <Box className="or-text">OR</Box>
                <Box className="divider-line" />
              </OrDivider>

              {/* Documentation Option */}
              <DocumentationButton onClick={handleDocumentationClick}>
                <Iconify 
                  icon="eva:book-open-fill" 
                  className="doc-icon"
                  sx={{ 
                    fontSize: 24,
                    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                  }} 
                />
                <Box sx={{ flex: 1 }}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 0.5 }}>
                    Read Documentation
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.4 }}>
                    Step-by-step guides and helpful tutorials
                  </Typography>
                </Box>
                <Iconify 
                  icon="eva:external-link-fill" 
                  className="external-icon"
                  sx={{ 
                    fontSize: 18,
                    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                  }} 
                />
              </DocumentationButton>
            </Stack>
          </CardContent>
        </StyledCard>
      </Zoom>

      <Zoom in timeout={200}>
        <StyledFab open={open} onClick={handleToggle} aria-label="help">
          <Iconify 
            icon={open ? "eva:close-fill" : "eva:question-mark-circle-fill"} 
            sx={{ 
              fontSize: 24,
              transition: 'transform 0.2s ease-in-out',
              transform: open ? 'rotate(90deg)' : 'rotate(0deg)',
            }} 
          />
        </StyledFab>
      </Zoom>
    </>
  );
};

export default HelpWidget;